import React, { useEffect, useRef } from 'react';
import { Music, ArrowRight, Star, Pin, Users, ArrowLeft } from 'lucide-react';
import gsap from 'gsap';
import { TEAM_MEMBERS } from '../constants';

interface AboutProps {
  onNavigate: (page: string) => void;
}

const About: React.FC<AboutProps> = ({ onNavigate }) => {
  const textContainerRef = useRef<HTMLDivElement>(null);
  const teamSectionRef = useRef<HTMLDivElement>(null);

  const auroraStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(to right, hsla(228, 51%, 70%, 1) 0%, hsla(27, 90%, 68%, 1) 45%, hsla(29, 100%, 49%, 1) 50%, hsla(27, 90%, 68%, 1) 55%, hsla(228, 51%, 70%, 1) 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  useEffect(() => {
    // GSAP Animation
    if (textContainerRef.current) {
        const elements = textContainerRef.current.children;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        gsap.fromTo(elements, 
                            { y: 20, opacity: 0 },
                            { y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: "power2.out" }
                        );
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.1 }
        );
        observer.observe(textContainerRef.current);
        
        return () => observer.disconnect();
    }
  }, []);

  // Handle Hash Scroll
  useEffect(() => {
    if (window.location.hash === '#team' && teamSectionRef.current) {
        setTimeout(() => {
            teamSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
            // Clean up hash without jumping
            history.replaceState(null, '', window.location.pathname);
        }, 100);
    }
  }, []);

  return (
    <section className="px-6 py-12 md:py-20 relative overflow-hidden min-h-[80vh]">
      
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => onNavigate('home')}
          className="mb-8 flex items-center gap-2 text-slate-500 hover:text-primary transition-colors font-mono text-[10px] uppercase tracking-[0.2em] group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        {/* Header Section */}
        <div className="mb-16 border-l-4 border-primary pl-6 md:pl-10 relative">
          <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 bg-primary rounded-full shadow-[0_0_10px_rgba(249,115,22,0.8)]"></div>
          <h1 
            className="text-5xl md:text-7xl font-bold uppercase font-display tracking-tighter mb-4 animate-aurora"
            style={auroraStyle}
          >
            Our Mission
          </h1>
          <p className="font-mono text-primary text-xs md:text-sm tracking-[0.2em] uppercase">
            EST. 2025 // ORIGIN: NEW YORK
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Main Text Content */}
          <div ref={textContainerRef} className="lg:col-span-8 space-y-8 font-mono text-sm md:text-base leading-relaxed text-slate-800 dark:text-white">
            <p className="opacity-0 translate-y-4">
              <strong className="text-slate-900 dark:text-white font-bold">
                <Star className="text-primary fill-primary inline-block mr-2 align-text-bottom mb-0.5" size={18} />
                Starroad Touring
              </strong> is a leading touring company dedicated to connecting artists with audiences through memorable live experiences. Founded in 2025, we specialize in organizing and promoting a diverse range of musical performances and events.
            </p>
            <p className="opacity-0 translate-y-4">
              Our mission is to elevate the live music experience by providing upcoming artists with the opportunity to showcase their talent. 
              Star Road Touring operates at the intersection of talent scouting and stadium logistics. We don’t fill slots; we curate the energy of the opening set to ensure the headliner’s night is a success. We manage the placements, the clearances, and the road-readiness of the next generation of global acts.
            </p>
            <p className="font-bold text-primary/90 opacity-0 translate-y-4">
                You bring the talent. We provide the circuit.
            </p>
            <p className="opacity-0 translate-y-4">
              At Starroad Touring, we pride ourselves on our collaborative spirit and strong partnerships. We work closely with artists, venues, and global logistics teams to synchronize every deployment and digital platforms like Audiomack to maximize visibility and reach. Our team is passionate about music, dedicated to nurturing talent, and committed to delivering high-quality events that resonate with audiences.
            </p>

            <div className="pt-8 opacity-0 translate-y-4">
                {/* Sticky Note Design */}
                <div className="bg-[#fff9c4] text-black p-6 md:p-8 shadow-[5px_5px_15px_rgba(0,0,0,0.3)] rotate-2 transform transition-transform hover:rotate-1 hover:scale-[1.02] relative mx-4 md:mx-0 mt-4 max-w-xl">
                    {/* Pin effect */}
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 filter drop-shadow-md">
                        <Pin className="text-red-600 fill-red-600" size={32} />
                    </div>

                    <div className="absolute top-2 right-2 p-2 opacity-10">
                        <Music size={40} className="text-black" />
                    </div>
                    <p className="font-display font-bold text-lg md:text-xl italic relative z-10 leading-snug text-black">
                        "We don't just book shows; we curate the next generation of headliners. By placing high-potential openers on major tours, we turn rising stars into household names."
                    </p>
                </div>
            </div>

            {/* TEAM SECTION */}
            <div id="team" ref={teamSectionRef} className="pt-16 opacity-0 translate-y-4">
                <div className="flex items-center gap-4 mb-10">
                    <div className="h-px bg-slate-200 dark:bg-slate-700 flex-grow"></div>
                    <h2 className="text-2xl font-bold uppercase font-display text-slate-900 dark:text-white flex items-center gap-2">
                        <Users className="text-primary" size={24} />
                        Meet The Team
                    </h2>
                    <div className="h-px bg-slate-200 dark:bg-slate-700 flex-grow"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {TEAM_MEMBERS.map((member, index) => (
                        <div key={index} className="group flex flex-col items-start p-4 border border-slate-200 dark:border-slate-800 bg-white/5 dark:bg-slate-900/50 rounded-sm hover:border-primary/50 transition-colors">
                            <div className="w-full aspect-[3/4] overflow-hidden mb-4 bg-slate-800 relative">
                                <img 
                                    src={member.image} 
                                    alt={member.name} 
                                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                            </div>
                            <h3 className="font-display font-bold text-lg uppercase text-slate-900 dark:text-white mb-1">
                                {member.name}
                            </h3>
                            <span className="font-mono text-[10px] font-bold text-primary uppercase mb-3 block">
                                {member.role}
                            </span>
                            <p className="text-xs text-slate-600 dark:text-slate-400 font-mono leading-relaxed">
                                {member.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-4 lg:sticky lg:top-24">
             <div className="p-6 border border-slate-200 dark:border-slate-800 rounded-sm bg-slate-50/50 dark:bg-slate-900/50">
                <h3 className="font-display font-bold text-2xl uppercase mb-4 text-slate-900 dark:text-white">Get in Touch</h3>
                <p className="font-mono text-xs text-slate-500 mb-6 leading-relaxed">
                    Ready to take your performance to the next stage? Contact our team for booking inquiries and partnership opportunities.
                </p>
                <button 
                  onClick={() => onNavigate('submission')}
                  className="w-full bg-primary hover:bg-orange-600 text-white font-mono font-bold py-4 px-8 flex items-center justify-between transition-all group rounded-sm shadow-[0_4px_14px_0_rgba(249,115,22,0.39)] hover:shadow-[0_6px_20px_rgba(249,115,22,0.23)] hover:-translate-y-1"
                >
                    <span>CONTACT US</span>
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </button>
             </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;